import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PackageBox, PackageDetail } from '@azko/shared';

import { ApiRequestError, api } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { statusLabel, statusTone } from '../lib/format.js';
import { Alert, Button, Spinner, StatusBadge, TextInput } from './ui.js';

/**
 * O detalhe de uma encomenda, aberto na propria linha da lista.
 *
 * Nasceu como comprovante de retirada e cresceu pelo mesmo motivo que o
 * originou: o produto GRAVA prova e nao tinha onde mostrar. A assinatura
 * existia desde a migration 0006 e a foto do pacote desde a 0009, as duas com
 * `GET` autenticado e nenhuma tela que chamasse. Eram dois recursos vendidos,
 * cobrados no plano, coletados no balcao — e invisiveis.
 *
 * Os dois momentos em que isto importa sao opostos e igualmente tardios: a
 * FOTO responde semanas depois "a caixa ja chegou amassada?", quando o morador
 * reclama do estado; a ASSINATURA responde "quem levou?", quando ele diz que
 * nunca recebeu. Ate aqui as duas respostas existiam no banco e a portaria so
 * podia jurar.
 *
 * Carrega sob demanda, quando a linha e aberta. Sao centenas de KB por
 * encomenda entre foto e assinatura, e trazer tudo junto com a lista seria
 * pagar por vinte para ver uma.
 */
export function PackageDetails({ packageId }: { packageId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['packages', 'detail', packageId],
    queryFn: () => api.get<PackageDetail>(`/packages/${packageId}`),
  });

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted">
        <Spinner /> Carregando os detalhes...
      </p>
    );
  }

  if (error || !data) {
    return (
      <Alert tone="warn">
        {error instanceof ApiRequestError ? error.message : 'Nao consegui carregar os detalhes.'}
      </Alert>
    );
  }

  const retirada = data.status === 'picked_up';

  return (
    <div className="rounded-2xl border border-line bg-surface-2 p-4">
      <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
        <Fact label="Recebida por" value={data.receivedByName} />
        <Fact label="Estado na chegada" value={conditionLabel(data.condition)} />
        {retirada && <Fact label="Retirada por" value={data.deliveredToName} />}
        {retirada && (
          <Fact label="Quando" value={data.pickedUpAt ? formatMoment(data.pickedUpAt) : null} />
        )}
        {retirada && <Fact label="Entregue por" value={data.deliveredByName} />}
      </dl>

      {/* So em lote. Encomenda simples nao tem sub-estrutura para mostrar, e uma
          caixa unica desenhada como "caixa 1 de 1" seria ruido em quase toda
          abertura deste painel. */}
      {data.boxCount > 1 && <BoxList boxes={data.boxes} />}

      {data.notes && (
        <div className="mt-4 border-t border-line pt-4">
          <Label>Observacoes da portaria</Label>
          <p className="mt-1 text-sm">{data.notes}</p>
        </div>
      )}

      <div className="mt-4 border-t border-line pt-4">
        <Label>Como o pacote chegou</Label>
        {data.photoAt ? (
          <ApiImage
            path={`/packages/${packageId}/foto`}
            alt="Foto do pacote na chegada"
            collectedAt={data.photoAt}
            className="mt-2 max-h-72 w-full rounded-xl border border-line object-contain"
          />
        ) : (
          <p className="mt-2 text-sm text-faint">Esta encomenda foi registrada sem foto.</p>
        )}
      </div>

      {retirada && (
        <div className="mt-4 border-t border-line pt-4">
          <Label>Assinatura</Label>
          {data.signedAt ? (
            <ApiImage
              path={`/packages/${packageId}/signature`}
              alt="Assinatura de quem retirou a encomenda"
              collectedAt={data.signedAt}
              className="mt-2 h-32 w-full max-w-md rounded-xl bg-white object-contain p-2"
            />
          ) : (
            <p className="mt-2 text-sm text-faint">
              Esta entrega foi confirmada sem assinatura.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * As caixas do lote, uma linha cada.
 *
 * E a tela que justifica o desenho de pai-com-filhas ter custado uma tabela de
 * relacoes em vez de uma coluna `quantity`: com um numero na linha, nao haveria
 * onde mostrar que a caixa 2 saiu ontem, a 1 continua no armario A3 e a 3 foi
 * cancelada porque era do vizinho. Cada caixa tem status, lugar e etiqueta
 * proprios porque no mundo real elas divergem.
 *
 * O botao de remover e por CAIXA, e nao repete o do registro: cancelar o
 * registro inteiro (na lista) derruba tudo o que aguarda; aqui se desfaz um
 * volume so, sem tocar nos outros nem no QR do morador.
 */
function BoxList({ boxes }: { boxes: PackageBox[] }) {
  const queryClient = useQueryClient();
  const { allows } = useSession();
  const [removing, setRemoving] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [failure, setFailure] = useState<string | null>(null);

  const canCancel = allows('encomendas:cancelar');

  const cancel = useMutation({
    mutationFn: (boxId: string) =>
      api.post<PackageDetail>(`/packages/${boxId}/cancelar`, { reason: reason.trim() }),
    onSuccess: () => {
      setRemoving(null);
      setReason('');
      setFailure(null);
      // A contagem de caixas do registro mudou, e com ela a linha da lista e os
      // cards da visao geral.
      void queryClient.invalidateQueries({ queryKey: ['packages'] });
    },
    onError: (cause) => {
      setFailure(
        cause instanceof ApiRequestError ? cause.message : 'Nao consegui cancelar esta caixa.',
      );
    },
  });

  return (
    <div className="mt-4 border-t border-line pt-4">
      <Label>Caixas deste registro</Label>

      <ul className="mt-2 space-y-2">
        {boxes.map((box) => (
          <li key={box.id} className="rounded-xl border border-line bg-surface p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-bold">Caixa {box.seq}</span>
                <span className="text-muted">#{box.code}</span>
                <StatusBadge tone={statusTone(box.status)}>
                  {statusLabel(box.status)}
                </StatusBadge>
              </span>

              {canCancel && box.status === 'awaiting' && (
                <Button
                  variant="quiet"
                  className="py-1"
                  onClick={() => {
                    setRemoving((open) => (open === box.id ? null : box.id));
                    setReason('');
                    setFailure(null);
                  }}
                >
                  {removing === box.id ? 'Fechar' : 'Remover'}
                </Button>
              )}
            </div>

            <p className="mt-1 text-sm text-muted">
              {box.storageLocation || 'Sem local definido'}
              {box.trackingCode && ` • ${box.trackingCode}`}
              {box.condition !== 'normal' && ` • ${conditionLabel(box.condition)}`}
              {box.pickedUpAt && ` • retirada em ${formatMoment(box.pickedUpAt)}`}
            </p>

            {removing === box.id && (
              <div className="mt-3 rounded-xl border border-warn/40 bg-warn/10 p-3">
                <p className="text-sm">
                  Cancela so a caixa {box.seq}. As outras continuam aguardando e o QR do
                  morador segue valendo.
                </p>

                <TextInput
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Motivo (obrigatorio)"
                  aria-label={`Motivo do cancelamento da caixa ${box.seq}`}
                  maxLength={200}
                  className="mt-2"
                />

                {failure && (
                  <div className="mt-2">
                    <Alert>{failure}</Alert>
                  </div>
                )}

                <Button
                  className="mt-2 py-1.5"
                  // O mesmo minimo da API: melhor o botao ficar desligado do que
                  // a portaria levar um 422 depois de confirmar.
                  disabled={reason.trim().length < 3}
                  loading={cancel.isPending}
                  onClick={() => cancel.mutate(box.id)}
                >
                  Confirmar
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Label({ children }: { children: string }) {
  return (
    <p className="text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-muted">{children}</p>
  );
}

function Fact({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-muted">
        {label}
      </dt>
      <dd className="mt-0.5 font-semibold">{value || '—'}</dd>
    </div>
  );
}

function formatMoment(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function conditionLabel(condition: PackageBox['condition']): string {
  return condition === 'damaged' ? 'Avariada' : condition === 'wet' ? 'Molhada' : 'Boa';
}

/**
 * Uma imagem que mora atras da API.
 *
 * Passa pelo `apiBlobUrl` porque as rotas exigem o header `X-Condominium`, que
 * um `<img src>` nao manda — o navegador envia o cookie de sessao e mais nada,
 * e a API recusa sem saber de qual condominio e a request.
 *
 * `collectedAt` chega junto porque a AUSENCIA da imagem tem dois significados
 * opostos e a rota responde 404 nos dois. Se a data existe, o arquivo foi
 * coletado e o que sumiu foi so ele — expurgado pela retencao do plano. Chamar
 * isso de erro faria o sindico abrir chamado por um comportamento que ele
 * contratou.
 *
 * A foto escurece bem e fica sobre o proprio fundo; a assinatura vai sobre
 * BRANCO porque o traco e preto sobre transparente e sumiria no tema escuro do
 * app. Por isso o `className` vem de fora em vez de morar aqui.
 */
function ApiImage({
  path,
  alt,
  collectedAt,
  className,
}: {
  path: string;
  alt: string;
  collectedAt: string;
  className: string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    let active = true;
    let created: string | null = null;

    api
      .blobUrl(path)
      .then((objectUrl) => {
        created = objectUrl;
        // Se o painel fechou antes de a imagem chegar, a URL nasce e morre
        // aqui mesmo — sem isto ela vazaria sem nunca ter sido exibida.
        if (active) setUrl(objectUrl);
        else URL.revokeObjectURL(objectUrl);
      })
      .catch((cause: unknown) => {
        if (!active) return;

        if (cause instanceof ApiRequestError && cause.status === 404) {
          setExpired(true);
          return;
        }

        setFailed(
          cause instanceof ApiRequestError ? cause.message : 'Nao consegui carregar a imagem.',
        );
      });

    return () => {
      active = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [path]);

  if (expired) {
    return (
      <p className="mt-2 text-sm text-muted">
        Coletada em{' '}
        <span className="font-semibold text-fg">
          {new Date(collectedAt).toLocaleDateString('pt-BR')}
        </span>
        . A imagem foi apagada pelo prazo de retencao do plano.
      </p>
    );
  }

  if (failed) {
    return (
      <div className="mt-2">
        <Alert tone="warn">{failed}</Alert>
      </div>
    );
  }

  if (!url) {
    return (
      <p className="mt-2 flex items-center gap-2 text-sm text-muted">
        <Spinner /> Carregando...
      </p>
    );
  }

  return <img src={url} alt={alt} className={className} />;
}
